import { useParams, useNavigate } from 'react-router-dom'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  LoadingPage,
  UserStatusBadge,
  UserRoleBadge,
  ButtonVariant,
  UserIcon,
  UserCard,
} from '@valiant/ui-shared'
import { useGetUserQuery, UserRole } from '@/generated/graphql'
import { useEffect, useMemo } from 'react'
import { LogsTab } from '@/components/tabs/LogsTab'
import { useAuth } from '@/AuthProvider'
import { useQueryParams } from '@valiant/ui-shared'
import { MainLayout } from '@/components/layout/MainLayout'
import { Settings } from 'lucide-react'

export function ComplianceOfficer() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryParams = useQueryParams('logs')
  const [{ data: userData, fetching: userLoading, error: userError }] = useGetUserQuery({
    variables: { userId: userId! },
    pause: !userId,
  })
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const user = userData?.getUser

  useEffect(() => {
    if (user && user.role !== UserRole.Compliance) {
      navigate('/not-found')
    }
  }, [user, navigate])

  const badges = useMemo(() => {
    if (!user) return []
    return [
      {
        node: <UserRoleBadge role={user.role} />,
        direction: 'LEFT',
      },
      {
        node: (
          <UserStatusBadge
            isWhitelisted={user.isWhitelisted}
            isTrader={user.role === UserRole.Trader}
            isActive={user.isActive}
          />
        ),
        direction: 'LEFT',
      },
    ]
  }, [user])

  const buttons = useMemo(() => {
    if (!user || isComplianceUser) return []
    return [
      {
        label: 'Manage',
        icon: <Settings className="h-3 w-3 md:h-4 md:w-4" />,
        variant: ButtonVariant.MAIN_ACTION,
        onClick: () => navigate(`/users/${user.id}/settings`),
      },
    ]
  }, [user, navigate, isComplianceUser])

  if (userLoading) {
    return <LoadingPage />
  }

  if (userError || !user) {
    return (
      <MainLayout>
        <div>Compliance officer not found</div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: user.username,
          icon: <UserIcon />,
          hideImage: true,
        },
      ]}
      title={user.username}
      badges={badges}
      buttons={buttons}
    >
      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <UserCard email={user.email} role={user.role} />
      </div>
      {/* Tabs Section */}
      <Tabs
        value={queryParams.tab || 'logs'}
        onValueChange={queryParams.updateTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="logs">Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="logs">
          <LogsTab userId={user.id} />
        </TabsContent>
      </Tabs>{' '}
    </MainLayout>
  )
}
